import { QIcon_chevron_bold_left } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { getEnv } from '../../../modules/envUtil'
import { IS_PRODUCTION } from '../../../modules/envVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import { LOCALE_EN, LOCALE_KO } from '../../../modules/i18n/config'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import { HelpCenterComponent } from '../../../pages/[lang]/portal/help-center'
import { fetchSubMenu } from '../../../pages/[lang]/student/landing/blog'
import { useHelpCenterStack } from '../../../pages/[lang]/webview/help-center'
import { HELP_CENTER_GET_MENU_DATA_QUERY } from '../../../sanity/groqQueries'
import { HELP_CENTER_GET_MENU_DATA_QUERYResult } from '../../../sanity/types'
import { IMenuQuery, ISubMenuQuery } from '../../landing/blog/type'
import WebviewStackContainer from '../common/WebviewStackContainer'

const fetchMenu = async (lang, ids) => {
  const params = {
    lang: lang,
    ids: ids,
  }

  const menu: HELP_CENTER_GET_MENU_DATA_QUERYResult[] = await client.fetch(HELP_CENTER_GET_MENU_DATA_QUERY, params)
  return menu
}

const HelpCenterActivity: ActivityComponentType = () => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const { useHelpCenterFlow } = useHelpCenterStack()
  const { pop } = useHelpCenterFlow()
  const { isLocaleKO } = useTranslation()
  const ids = [
    '6d3f4671-7469-4126-b10d-761354f4b8c8',
    'c7cb2ce8-d08c-4e87-a1da-df0702c925c9',
    '16f44e8b-a228-484a-a3a2-a3fd1507d8d9',
    'd9eba279-0116-4764-9e81-39f6c7fa286b',
  ]

  const { data: menu }: IMenuQuery = useQuery({
    queryKey: ['menu', isLocaleKO ? LOCALE_KO : LOCALE_EN],
    queryFn: () => fetchMenu(isLocaleKO ? LOCALE_KO : LOCALE_EN, ids),
  })

  const getLanguageCode = useCallback((lang) => {
    switch (lang) {
      case 'kr':
        return 'ko'
      case 'zh_chs':
        return 'zh'
      case 'zh_cht':
        return 'de'
      default:
        return lang
    }
  }, [])

  const lang = useMemo(() => getLanguageCode(router.query.lang), [router.query.lang, getLanguageCode])

  useEffect(() => {
    if (!IS_PRODUCTION) return

    const baseConfig = {
      pluginKey: getEnv('NEXT_PUBLIC_PLUS_CHANNEL_IO_KEY'),
      language: lang,
      memberId: currentUser?.id,
      hideChannelButtonOnBoot: false,
      profile: {
        name: currentUser?.profile.name,
        email: currentUser?.profile.email,
        mobileNumber: currentUser?.profile.mobileNumber,
        avatarUrl: currentUser?.image_url,
        finished_lesson_count: currentUser?.profile.finished_lesson_count,
        point: currentUser?.student_card.point,
        unused_coupons: currentUser?.unused_coupons,
        purchase_status: currentUser?.braze?.purchase?.status,
        last_purchased_date: currentUser?.braze?.purchase?.last_purchased_date,
        last_purchased_name: currentUser?.braze?.purchase?.last_purchased_name,
        customer_type: currentUser?.braze?.private?.customer_type,
        last_lesson_start_time: currentUser?.braze?.lesson?.last_lesson_start_time,
      },
    }

    console.log('BOOT_CHANNEL_TALK : ', baseConfig)
    window.ChannelIO?.('boot', baseConfig)

    setTimeout(() => {
      window.ChannelIO?.('showChannelButton')
    }, 100)
    if (router.query.showMessenger) {
      window.ChannelIO?.('showMessenger')
    }
    return () => {
      window.ChannelIO?.('shutdown')
    }
  }, [lang, currentUser, router.asPath])

  useEffect(() => {
    setTimeout(() => {
      window.ChannelIO?.('showChannelButton')
    }, 100)
  }, [])

  const { data: subMenu }: ISubMenuQuery = useQuery({ queryKey: ['subMenu', router.query.lang], queryFn: fetchSubMenu })

  return (
    <WebviewStackContainer height="0px" onBack={pop}>
      <div className="border-b-1 flex h-[54px] items-center border-gray-200 px-[16px]">
        <div onClick={() => router.back()}>
          <QIcon_chevron_bold_left width={20} height={20} />
        </div>
      </div>
      {menu && <HelpCenterComponent menu={menu} subMenu={subMenu} />}
    </WebviewStackContainer>
  )
}

export default HelpCenterActivity
